import { useForm, useSelect } from '@refinedev/antd';
import { Edit } from '@refinedev/antd';
import { Form, Input, Checkbox, Card, Row, Col, Divider, Switch } from 'antd';
import { LabelWithHelp, SectionTitle } from '../../components/ui/HelpTooltip';
import { t } from '../../i18n';

const permissionGroups = [
  {
    title: t('staff.permissionGroups.users'),
    key: 'users',
    permissions: ['read', 'update', 'block'],
    help: "Foydalanuvchilar ro'yxati va ularni boshqarish",
  },
  {
    title: t('staff.permissionGroups.drivers'),
    key: 'drivers',
    permissions: ['read', 'update', 'verify', 'block'],
    help: "Haydovchilar ro'yxati, tasdiqlash va boshqarish",
  },
  {
    title: t('staff.permissionGroups.documents'),
    key: 'documents',
    permissions: ['read', 'review'],
    help: "Haydovchi hujjatlarini ko'rish va tasdiqlash",
  },
  {
    title: t('staff.permissionGroups.trips'),
    key: 'trips',
    permissions: ['read', 'update', 'cancel'],
    help: "Sayohatlarni ko'rish, tahrirlash va bekor qilish",
  },
  {
    title: t('staff.permissionGroups.payments'),
    key: 'payments',
    permissions: ['read'],
    help: "To'lovlar tarixini ko'rish",
  },
  {
    title: t('staff.permissionGroups.support'),
    key: 'support',
    permissions: ['read', 'reply', 'resolve'],
    help: "Foydalanuvchi murojatlarini ko'rish va javob berish",
  },
  {
    title: t('staff.permissionGroups.faq'),
    key: 'faq',
    permissions: ['read', 'create', 'update', 'delete'],
    help: "Ko'p so'raladigan savollarni boshqarish",
  },
  {
    title: t('staff.permissionGroups.promo'),
    key: 'promo',
    permissions: ['read', 'create', 'update', 'delete'],
    help: "Chegirma kodlarini yaratish va boshqarish",
  },
  {
    title: 'Marketing havolalari',
    key: 'tracking-links',
    permissions: ['read', 'create', 'update', 'delete'],
    help: 'Marketing kanallari uchun kuzatiladigan havolalarni yaratish va statistikasini ko‘rish',
  },
  {
    title: t('staff.permissionGroups.settings'),
    key: 'settings',
    permissions: ['read', 'update'],
    help: "Tizim sozlamalarini o'zgartirish",
  },
  {
    title: t('staff.permissionGroups.staff'),
    key: 'staff',
    permissions: ['read', 'create', 'update', 'delete'],
    help: "Admin xodimlarni yaratish va boshqarish",
  },
];

const permissionLabels: Record<string, string> = {
  read: t('staff.actions.read'),
  create: t('staff.actions.create'),
  update: t('staff.actions.update'),
  delete: t('staff.actions.delete'),
  block: t('staff.actions.block'),
  verify: t('staff.actions.verify'),
  review: t('staff.actions.review'),
  reply: t('staff.actions.reply'),
  resolve: t('staff.actions.resolve'),
  cancel: t('staff.actions.cancel'),
};

export const StaffEdit = () => {
  const { formProps, saveButtonProps, queryResult } = useForm({
    resource: 'admin/staff',
    redirect: 'show',
  });

  const staff = queryResult?.data?.data;

  return (
    <Edit saveButtonProps={saveButtonProps} title={`Xodimni tahrirlash: ${staff?.fullName || staff?.username || ''}`}>
      <Form {...formProps} layout="vertical">
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title={t('staff.basicInfo')}
                  help={t('staff.basicInfo')}
                />
              }
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.login')}
                    help="Xodim admin panelga kirish uchun foydalanadigan login"
                  />
                }
                name="username"
              >
                <Input disabled placeholder="Login o'zgartirib bo'lmaydi" />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.newPassword')}
                    help="Yangi parol o'rnatish uchun kiriting. Bo'sh qoldirsangiz parol o'zgarmaydi."
                  />
                }
                name="password"
                rules={[
                  { min: 6, message: "Kamida 6 ta belgi bo'lishi kerak" },
                ]}
              >
                <Input.Password placeholder="Yangi parol (ixtiyoriy)" />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.fullName')}
                    help="Xodimning to'liq ismi va familiyasi"
                  />
                }
                name="fullName"
              >
                <Input placeholder="Abdullayev Abdulla" />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.phone')}
                    help={t('staff.phone')}
                  />
                }
                name="phone"
              >
                <Input placeholder="+998901234567" />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.activeStatus')}
                    help="Xodimning tizimga kirish huquqi"
                  />
                }
                name="isActive"
                valuePropName="checked"
              >
                <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title={t('staff.permissions')}
                  help="Xodimga qaysi bo'limlarga kirish va qanday amallar bajarishga ruxsat beriladi"
                />
              }
            >
              {permissionGroups.map((group) => (
                <div key={group.key} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <LabelWithHelp label={group.title} help={group.help} />
                  </div>
                  <Form.Item name={['permissions', group.key]} noStyle>
                    <Checkbox.Group>
                      {group.permissions.map((perm) => (
                        <Checkbox key={perm} value={perm}>
                          {permissionLabels[perm] || perm}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>
                  <Divider style={{ margin: '12px 0' }} />
                </div>
              ))}
            </Card>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
